import { useCallback } from 'react'

import { createSimulation, updateSimulation } from '@/features/domain/simulation'

import { useResetEditingState } from '@/atoms'
import { useIsUnmounted, useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'

import * as Texts from '../../../intl'
import type { SimulationFormValues } from '../typings'
import { computeCreationPayload, emptySimulationId } from '../utils'
import { setSimulationOrderLimitError } from './simulationOrderLimitError'

export const useOnSubmit = () => {
  const stopEditing = useResetEditingState()
  const isUnMounted = useIsUnmounted()
  const dispatch = useAppDispatch()
  const toast = useNotification()

  const onCreate = useCallback(
    async (formValues: SimulationFormValues) => {
      try {
        // Clear any previous order limit error
        setSimulationOrderLimitError(null)

        const simulationPayload = computeCreationPayload(formValues)
        const thunkResult = await dispatch(createSimulation(simulationPayload))

        if (!createSimulation.fulfilled.match(thunkResult)) {
          if (
            thunkResult.payload?.type === 'rpcFailure' &&
            thunkResult.payload.error?.errorCode === 603
          ) {
            setSimulationOrderLimitError(`${Texts.getSimulationValidationOrderLimitText()}`)
            return
          }
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        if (isUnMounted()) return

        stopEditing()
      } catch (e) {
        if (!isUnMounted()) {
          toast.error(e.message)
        }
      }
    },
    [dispatch, isUnMounted, stopEditing, toast],
  )

  const onUpdate = useCallback(
    async (formValues: SimulationFormValues) => {
      try {
        const payload = {
          simulationId: formValues.id,
          name: formValues.name,
          label: formValues.label,
        }
        const thunkResult = await dispatch(updateSimulation(payload))

        if (!updateSimulation.fulfilled.match(thunkResult)) {
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        if (isUnMounted()) return

        stopEditing()
      } catch (e) {
        if (!isUnMounted()) {
          toast.error(e.message)
        }
      }
    },
    [dispatch, isUnMounted, stopEditing, toast],
  )

  return useCallback(
    (formValues: SimulationFormValues) =>
      formValues.id === emptySimulationId ? onCreate(formValues) : onUpdate(formValues),
    [onCreate, onUpdate],
  )
}
